import { useCallback, useEffect, useRef, useState } from "react"

interface UseAudioRecorderReturn {
  isRecording: boolean
  duration: number
  error: string | null
  start: () => Promise<void>
  stop: () => Promise<Blob | null>
  cancel: () => void
  getFrequencyData: () => Uint8Array
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const frequencyDataRef = useRef<Uint8Array>(new Uint8Array(32))
  const mimeTypeRef = useRef<string>("audio/webm")

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close()
      audioContextRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
    setDuration(0)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  const start = useCallback(async () => {
    try {
      setError(null)
      cleanup()

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,
        },
      })
      streamRef.current = stream

      // Setup AudioContext and AnalyserNode for visualization
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 64
      analyser.smoothingTimeConstant = 0.8
      analyserRef.current = analyser

      const source = audioContext.createMediaStreamSource(stream)
      sourceRef.current = source
      source.connect(analyser)

      // Initialize frequency data array
      frequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount)

      // Setup MediaRecorder - try formats in order of browser playback compatibility
      let mimeType = "audio/webm" // fallback
      const preferredFormats = [
        "audio/mp4",
        "audio/mp4;codecs=mp4a.40.2", // AAC-LC
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/webm",
      ]

      for (const format of preferredFormats) {
        if (MediaRecorder.isTypeSupported(format)) {
          mimeType = format
          break
        }
      }

      console.log(`Using audio format: ${mimeType}`)
      mimeTypeRef.current = mimeType

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder

      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.start(100) // Collect data every 100ms
      setIsRecording(true)

      // Start duration timer
      const startTime = Date.now()
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        setDuration(elapsed)

        // Auto-stop at 60 seconds
        if (elapsed >= 60) {
          mediaRecorder.stop()
        }
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to access microphone"
      setError(message)
      cleanup()
    }
  }, [cleanup])

  const stop = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current

      if (!mediaRecorder || mediaRecorder.state === "inactive") {
        setIsRecording(false)
        cleanup()
        resolve(null)
        return
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeTypeRef.current,
        })
        setIsRecording(false)
        cleanup()
        resolve(blob)
      }

      mediaRecorder.stop()
    })
  }, [cleanup])

  const cancel = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop()
    }

    setIsRecording(false)
    cleanup()
  }, [cleanup])

  const getFrequencyData = useCallback((): Uint8Array => {
    if (analyserRef.current) {
      analyserRef.current.getByteFrequencyData(frequencyDataRef.current)
    }
    return frequencyDataRef.current
  }, [])

  return {
    isRecording,
    duration,
    error,
    start,
    stop,
    cancel,
    getFrequencyData,
  }
}
